import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { screenService } from "../../../../services/admin/screenService";

export default function AddScreenForm({ screenData, onSuccess }) {
  const [formData, setFormData] = useState({
    parentScreenId: "",
    screenName: "",
    screenUrl: "",
  });

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // ✅ Toast state
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 1500);
  };

  /* PREFILL WHEN EDITING */
  useEffect(() => {
    if (screenData) {
      setIsEditing(true);
      setFormData({
        parentScreenId: screenData.parentScreenId || "",
        screenName: screenData.screenName || "",
        screenUrl: screenData.screenURL || "",
      });
    } else {
      setIsEditing(false);
      setFormData({
        parentScreenId: "",
        screenName: "",
        screenUrl: "",
      });
    }
  }, [screenData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!formData.screenName.trim()) errs.screenName = 'Screen Name is required';
    if (!formData.screenUrl.trim()) errs.screenUrl = 'Screen URL is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    try {
      setLoading(true);

      const payload = {
        parentScreenId: Number(formData.parentScreenId) || 0,
        screenName: formData.screenName,
        screenURL: formData.screenUrl,
        modifiedBy: 0,
      };

      if (screenData) {
        await screenService.update(screenData.screenId, payload);
        showToast("Screen updated successfully", "success"); // ✅ update success
      } else {
        await screenService.create(payload);
        showToast("Screen created successfully", "success"); // ✅ create success
        setFormData({ parentScreenId: "", screenName: "", screenUrl: "" });
      }

      // ✅ Let toast show before notifying parent
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1600);

    } catch (error) {
      console.error("Save error:", error);
      showToast(error.message || "Operation failed. Please try again.", "error"); // ✅ catch error
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onSuccess) onSuccess();
  };

  return (
    <>
      <div className="ad-card ad-card-lg ad-animate">
        {/* HEADER */}
        <div className="ad-card-header">
          <h3 className="ad-card-title">
            {screenData ? "Edit Screen" : "Screen Master"}
          </h3>
        </div>

        <div className="ad-onboard-scroll">
          <div className="ad-grid-3">
            {/* PARENT SCREEN */}
            <div>
              <label className="ad-label">Parent Screen</label>
              <div className="ad-select-wrap">
                <select
                  className="ad-select"
                  name="parentScreenId"
                  value={formData.parentScreenId}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="1">Admin</option>
                  <option value="2">Services</option>
                </select>
              </div>
            </div>

            {/* SCREEN NAME */}
            <div>
              <label className="ad-label">Screen Name <span className="text-red-500">*</span></label>
              <input
                className={`ad-input${errors.screenName ? ' border-red-400' : ''}`}
                name="screenName"
                placeholder="Enter Screen Name"
                value={formData.screenName}
                onChange={handleChange}
              />
              {errors.screenName && <p className="text-red-500 text-xs mt-1">{errors.screenName}</p>}
            </div>

            {/* SCREEN URL */}
            <div>
              <label className="ad-label">Screen URL <span className="text-red-500">*</span></label>
              <input
                className={`ad-input${errors.screenUrl ? ' border-red-400' : ''}`}
                name="screenUrl"
                placeholder="Enter Screen URL"
                value={formData.screenUrl}
                onChange={handleChange}
              />
              {errors.screenUrl && <p className="text-red-500 text-xs mt-1">{errors.screenUrl}</p>}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ marginTop: "2rem", textAlign: "right", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            {screenData && (
              <button className="ad-btn ad-btn-gray" onClick={handleCancel} disabled={loading}>
                Cancel
              </button>
            )}
            <button className="ad-btn ad-btn-green" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : screenData ? "Update Screen" : "Save Screen"}
            </button>
          </div>
        </div>
      </div>

      {toast.visible && createPortal(
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"
          }`}>
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}>✕</span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>,
        document.body
      )}
    </>
  );
}